import produce from 'immer'
import { RacePageDTO, isRacePageDto } from '@mobi/api-types'
import { toDisplayPrice } from '@mobi/utils/money'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import type { TotePriceChangedPushEvent } from '@core/State/PushData'
import { logError } from '@core/Utils/logging/logging'
import type ObservableRacePage from '../../../../Model/Observables/ObservableRacePage'

interface PushTotePriceChanged {
  event: TotePriceChangedPushEvent
  meetingId: string
  meetingDate: Date
  raceNumber: number
  raceInformation: ObservableRacePage
}

export function pushTotePriceChanged({
  event,
  meetingId,
  meetingDate,
  raceNumber,
  raceInformation,
}: PushTotePriceChanged) {
  const queryKey = queryKeys.racePageComplete(meetingId, meetingDate, raceNumber)

  if (raceInformation.meetingInformation.selectedRace.key() !== event.raceId.toString()) {
    const raceKeyFromPage = raceInformation.meetingInformation.selectedRace.key()
    const raceKeyFromEvent = event.raceId.toString()
    logError(
      `pushTotePriceChanged: no update because race key does not match event (${raceKeyFromPage} vs ${raceKeyFromEvent})`,
      true
    )
    return undefined
  }

  // Update ReactQuery state
  queryClient.setQueryData<RacePageDTO | undefined>(queryKey, current => {
    if (!current) return undefined // key not found
    if (!isRacePageDto(current)) return undefined // only update open races

    return produce(current, draft => {
      const acceptorLookup = new Map(event.acceptorTotePools.map(p => [p.acceptorNumber, p]))
      draft.RaceStarters.flatMap(rs =>
        rs.RaceKey.RaceNumber === raceNumber ? rs.Starters : []
      ).forEach(starter => {
        const update = acceptorLookup.get(starter.Number)
        if (update) {
          starter.DisplayWinDividend = toDisplayPrice(update.winPrice)
          starter.DisplayPlaceDividend = toDisplayPrice(update.placePrice)
        }
        starter.IsFavourite = event.raceFavourite === starter.Number
      })
    })
  })

  // Update KO View Model state
  const starters = raceInformation.getStartersForRace(raceNumber)()

  event.acceptorTotePools.forEach(({ acceptorNumber, winPrice, placePrice }) => {
    const starter = starters.find(starter => starter.number() === acceptorNumber)
    if (starter) {
      if (winPrice !== null && winPrice !== undefined) {
        starter.displayWinDividend(toDisplayPrice(winPrice))
      }
      if (placePrice !== null && placePrice != undefined) {
        starter.displayPlaceDividend(toDisplayPrice(placePrice))
      }
      starter.isFavourite(starter.number() === event.raceFavourite)
    }
  })
}
